import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { getBackendUrl } from "@/lib/backend";
import { useScope } from "@/context/ScopeContext";
import "./UsmRiskSchoolsModal.css";

const API = `${getBackendUrl()}/api`;
const PAGE_SIZE = 12;

const fmt = (v) => (typeof v === "number" ? v.toLocaleString("en-IN") : v ?? "—");

const UsmRiskSchoolsModal = ({ open, risk, onClose, scopeLabel }) => {
  const navigate = useNavigate();
  const { scope } = useScope();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);

  useEffect(() => {
    if (!open || !risk?.id) return undefined;

    let cancelled = false;
    setLoading(true);
    setError(null);
    setData(null);
    setQuery("");
    setPage(1);

    const params = { risk_type: risk.id, limit: 500 };
    if (scope.districtCode) params.district_code = scope.districtCode;
    if (scope.blockCode) params.block_code = scope.blockCode;
    if (scope.udiseCode) params.udise_code = scope.udiseCode;
    if (scope.districtName) params.district_name = scope.districtName;
    if (scope.blockName) params.block_name = scope.blockName;
    if (!scope.districtName && !scope.districtCode) params.district_name = "PUNE";

    axios
      .get(`${API}/enrolment/risk-register-schools`, { params })
      .then((res) => {
        if (!cancelled) setData(res.data || null);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err.response?.data?.detail || "Failed to load school list");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, risk?.id, scope.version, scope.districtCode, scope.blockCode, scope.udiseCode, scope.districtName, scope.blockName]);

  const filtered = useMemo(() => {
    const schools = data?.schools || [];
    const q = query.trim().toLowerCase();
    if (!q) return schools;
    return schools.filter(
      (s) =>
        String(s.school_name || "").toLowerCase().includes(q) ||
        String(s.block_name || "").toLowerCase().includes(q) ||
        String(s.udise_code || "").includes(q),
    );
  }, [data?.schools, query]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageRows = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const parameterLabel = data?.parameter_label || risk?.parameterLabel || "Value";

  if (!open || !risk) return null;

  return createPortal(
    <div className="usm-risk-modal-overlay" role="presentation" onClick={onClose}>
      <div
        className="usm-risk-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="usm-risk-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="usm-risk-modal-head">
          <div>
            <p className="usm-risk-modal-eyebrow">Risk drill-down · {scopeLabel || "District scope"}</p>
            <h3 id="usm-risk-modal-title">{risk.risk}</h3>
            <p className="usm-risk-modal-sub">
              KPI trigger: <b>{risk.trigger}</b> · Aggregate value: <b>{risk.value}</b>
            </p>
          </div>
          <button type="button" className="usm-risk-modal-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className="usm-risk-modal-toolbar">
          <input
            type="search"
            className="usm-risk-modal-search"
            placeholder="Search school, block, or UDISE…"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setPage(1);
            }}
          />
          {data ? (
            <span className="usm-risk-modal-count">
              Showing {fmt(filtered.length)} of {fmt(data.total_schools)} schools
            </span>
          ) : null}
        </div>

        <div className="usm-risk-modal-body">
          {loading ? (
            <div className="usm-risk-modal-loading">Loading schools…</div>
          ) : error ? (
            <div className="usm-risk-modal-error">{error}</div>
          ) : !pageRows.length ? (
            <div className="usm-risk-modal-empty">No schools found for this risk in the current scope.</div>
          ) : (
            <table className="usm-risk-modal-table">
              <thead>
                <tr>
                  <th>School</th>
                  <th>Block</th>
                  <th>UDISE</th>
                  <th className="usm-num">{parameterLabel}</th>
                  {risk.id === "aided_teacher_pressure" ? (
                    <>
                      <th className="usm-num">Students</th>
                      <th className="usm-num">Teachers</th>
                    </>
                  ) : risk.id === "zero_teacher" ? (
                    <th className="usm-num">Students</th>
                  ) : risk.id === "low_enrolment" ? (
                    <th className="usm-num">Teachers</th>
                  ) : null}
                </tr>
              </thead>
              <tbody>
                {pageRows.map((school) => (
                  <tr
                    key={school.udise_code}
                    className="usm-risk-modal-row"
                    onClick={() => school.udise_code && navigate(`/school/${school.udise_code}`)}
                    title={school.udise_code ? "Open school detail" : undefined}
                  >
                    <td>{school.school_name || "—"}</td>
                    <td>{school.block_name || "—"}</td>
                    <td>{school.udise_code || "—"}</td>
                    <td className="usm-num">{school.parameter_value ?? "—"}</td>
                    {risk.id === "aided_teacher_pressure" ? (
                      <>
                        <td className="usm-num">{fmt(school.student_count)}</td>
                        <td className="usm-num">{fmt(school.teacher_count)}</td>
                      </>
                    ) : risk.id === "zero_teacher" ? (
                      <td className="usm-num">{fmt(school.student_count)}</td>
                    ) : risk.id === "low_enrolment" ? (
                      <td className="usm-num">{fmt(school.teacher_count)}</td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {!loading && !error && filtered.length > PAGE_SIZE ? (
          <div className="usm-risk-modal-footer">
            <button
              type="button"
              className="usm-risk-modal-page-btn"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </button>
            <span>
              Page {page} of {totalPages}
            </span>
            <button
              type="button"
              className="usm-risk-modal-page-btn"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              Next
            </button>
          </div>
        ) : null}
      </div>
    </div>,
    document.body,
  );
};

export default UsmRiskSchoolsModal;
